// Page copy for the redesigned Studio. Keep enUS and zhCN structurally identical.
// Support panel: conversations and tickets with Spatius. Category / platform /
// integration option labels and form validation stay on the existing `feedback.*` keys.
export const enUS = {
  title: 'Support',
  unreadLabel: 'Support, {n} unread',
  openSupport: 'Open Support',
  closeSupport: 'Close Support',
  region: {
    chats: 'Conversations with Spatius',
    tickets: 'Support tickets',
  },
  chats: {
    heading: 'What’s on your mind?',
    intro: 'Ask a question or share an idea — Spatius will reply here',
    viewTickets: 'View tickets',
    viewTicketsUnread: 'View tickets, {n} unread',
    newChat: 'New conversation',
    loading: 'Loading conversations…',
    loadFailed: 'Could not load conversations',
    empty: 'No conversations yet',
    emptyHint: 'Start a conversation and Spatius will reply here',
    allChats: 'All conversations',
    backToChats: 'Back to conversations',
    unread: 'Unread reply',
  },
  tickets: 'Tickets',
  allTickets: 'All tickets',
  backToTickets: 'Back to tickets',
  createTicket: 'Create ticket',
  viewChat: 'View conversations',
  viewChatUnread: 'View conversations, {n} unread',
  filter: {
    label: 'Ticket status',
    open: 'Open tickets ({n})',
    closed: 'Closed tickets ({n})',
  },
  search: {
    label: 'Search tickets',
    placeholder: 'Search tickets by title',
    clear: 'Clear search',
  },
  list: {
    loading: 'Loading tickets…',
    loadFailed: 'Could not load tickets',
    retry: 'Retry',
    noOpen: 'No open tickets',
    noOpenHint: 'Create a ticket to track an issue with our team',
    noClosed: 'No closed tickets',
    noClosedHint: 'Closed tickets stay here with their conversation history',
    noMatch: 'No matching tickets',
    noMatchHint: 'Try a different title',
    unread: 'Unread ticket update',
  },
  status: {
    OPEN: 'In progress',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed, not resolved',
  },
  time: {
    minutes: '{n}m ago',
    hours: '{n}h ago',
  },
  detail: {
    loading: 'Loading conversation…',
    loadFailed: 'Could not load this conversation',
    details: 'Ticket details',
    category: 'Category',
    created: 'Created',
    updated: 'Updated',
    platform: 'Platform',
    integration: 'Integration',
    conversation: 'Conversation: {subject}',
    you: 'You',
    team: 'Spatius',
    closeTicket: 'Close ticket',
    replyLabel: 'Reply to {subject}',
    ticketPlaceholder: 'Reply to this ticket…',
    chatPlaceholder: 'Write a message…',
    shortcuts: 'Enter to send. Shift + Enter for a new line.',
    sendReply: 'Send reply',
    sendMessage: 'Send message',
    sendHint: 'Send · Enter',
    sending: 'Sending…',
    resolvedNote: 'Ticket resolved · Conversation is read-only',
    closedNote: 'Ticket closed · Conversation is read-only',
    followUp: 'Create follow-up ticket',
    createTicket: 'Create ticket',
    unavailable: 'Conversation unavailable',
    unavailableHint: 'This conversation could not be found. Your others are still available.',
    viewTickets: 'View tickets',
    viewChats: 'View conversations',
    newReply: 'New reply from Spatius',
  },
  attachments: {
    images: 'Image attachments',
    files: 'File attachments',
    add: 'Add images or files',
    preparing: 'Uploading…',
    preview: 'Preview {name}',
    remove: 'Remove {name}',
    download: 'Download {name}',
    type: 'Choose a PNG, JPG, WebP or GIF image, or a ZIP or TXT file.',
    size: 'Choose a file smaller than 10 MB.',
    failed: 'This file could not be uploaded. Try again.',
    image: 'Image',
  },
  closeDialog: {
    title: 'Close this ticket?',
    body: 'You won’t be able to reply after closing it. Your conversation history will stay available. If you need more help, you can create a follow-up ticket.',
    question: 'Was your issue resolved?',
    keepOpen: 'Keep open',
    resolved: 'Resolved',
    unresolved: 'Not resolved',
  },
  form: {
    heading: 'Submit a ticket',
    intro: 'Give us the details. You’ll get a place to follow its progress.',
    category: 'Category',
    selectCategory: 'Select a category',
    avatar: 'Related avatar',
    avatarNone: 'Not about a specific avatar',
    avatarHint: 'We add its avatar ID to your ticket so our team can check this avatar.',
    avatarLoading: 'Loading your avatars…',
    avatarEmpty: 'No completed custom avatars yet',
    avatarLoadFailed: 'Could not load your avatars.',
    avatarRefreshFailed: 'Could not refresh your avatars. Showing the previously loaded list.',
    avatarDescriptionTooLong: 'With an avatar linked, the description can be up to {max} characters (now {count}). Shorten it, or choose “Not about a specific avatar”.',
    title: 'Title',
    titlePlaceholder: 'Briefly describe the problem',
    description: 'Description',
    descriptionPlaceholder: 'What happened? What did you expect? Add steps that help us reproduce it.',
    environment: 'Environment details',
    optional: 'Optional',
    platforms: 'Platforms',
    integrationModes: 'Integration mode',
    clearDraft: 'Clear draft',
    submit: 'Submit ticket',
    submitting: 'Submitting…',
  },
  chatForm: {
    heading: 'Start a conversation',
    intro: 'Ask a question or share an idea. Spatius will reply here.',
    subject: 'Subject',
    subjectPlaceholder: 'What is it about?',
    subjectRequired: 'Enter a subject.',
    message: 'Message',
    messagePlaceholder: 'Write your message…',
    messageRequired: 'Write a message or add an attachment.',
    clearDraft: 'Clear draft',
    submit: 'Send',
    submitting: 'Sending…',
  },
  tags: {
    publicAvatarApply: 'Public avatar application',
  },
}

export const zhCN = {
  title: '支持',
  unreadLabel: '支持，{n} 条未读',
  openSupport: '打开支持面板',
  closeSupport: '关闭支持面板',
  region: {
    chats: '与 Spatius 的会话',
    tickets: '支持工单',
  },
  chats: {
    heading: '有什么想聊的？',
    intro: '提问或分享想法，Spatius 会在这里回复你',
    viewTickets: '查看工单',
    viewTicketsUnread: '查看工单，{n} 条未读',
    newChat: '新建会话',
    loading: '正在加载会话…',
    loadFailed: '会话加载失败',
    empty: '还没有会话',
    emptyHint: '发起一个会话，Spatius 会在这里回复你',
    allChats: '全部会话',
    backToChats: '返回会话列表',
    unread: '有未读回复',
  },
  tickets: '工单',
  allTickets: '全部工单',
  backToTickets: '返回工单列表',
  createTicket: '创建工单',
  viewChat: '查看会话',
  viewChatUnread: '查看会话，{n} 条未读',
  filter: {
    label: '工单状态',
    open: '处理中的工单（{n}）',
    closed: '已关闭的工单（{n}）',
  },
  search: {
    label: '搜索工单',
    placeholder: '按标题搜索工单',
    clear: '清除搜索',
  },
  list: {
    loading: '正在加载工单…',
    loadFailed: '工单加载失败',
    retry: '重试',
    noOpen: '暂无处理中的工单',
    noOpenHint: '创建工单，跟进你遇到的问题',
    noClosed: '暂无已关闭的工单',
    noClosedHint: '已关闭的工单会连同对话记录保留在这里',
    noMatch: '没有匹配的工单',
    noMatchHint: '换个标题试试',
    unread: '工单有未读更新',
  },
  status: {
    OPEN: '处理中',
    RESOLVED: '已解决',
    CLOSED: '未解决关闭',
  },
  time: {
    minutes: '{n} 分钟前',
    hours: '{n} 小时前',
  },
  detail: {
    loading: '正在加载会话…',
    loadFailed: '无法加载这条会话',
    details: '工单详情',
    category: '类别',
    created: '创建时间',
    updated: '更新时间',
    platform: '平台',
    integration: '接入方式',
    conversation: '会话：{subject}',
    you: '你',
    team: 'Spatius',
    closeTicket: '关闭工单',
    replyLabel: '回复：{subject}',
    ticketPlaceholder: '回复这条工单…',
    chatPlaceholder: '输入消息…',
    shortcuts: 'Enter 发送，Shift + Enter 换行。',
    sendReply: '发送回复',
    sendMessage: '发送消息',
    sendHint: '发送 · Enter',
    sending: '发送中…',
    resolvedNote: '工单已解决 · 对话为只读',
    closedNote: '工单已关闭 · 对话为只读',
    followUp: '创建后续工单',
    createTicket: '创建工单',
    unavailable: '会话不可用',
    unavailableHint: '找不到这条会话，你的其他会话仍然可用。',
    viewTickets: '查看工单',
    viewChats: '查看会话',
    newReply: 'Spatius 有新回复',
  },
  attachments: {
    images: '图片附件',
    files: '文件附件',
    add: '添加图片或文件',
    preparing: '正在上传…',
    preview: '预览 {name}',
    remove: '移除 {name}',
    download: '下载 {name}',
    type: '请选择 PNG、JPG、WebP、GIF 图片，或 ZIP、TXT 文件。',
    size: '请选择小于 10 MB 的文件。',
    failed: '文件上传失败，请重试。',
    image: '图片',
  },
  closeDialog: {
    title: '关闭这条工单？',
    body: '关闭后将无法继续回复，对话记录会保留。如果还需要帮助，可以创建后续工单。',
    question: '你的问题解决了吗？',
    keepOpen: '保持打开',
    resolved: '已解决，关闭',
    unresolved: '未解决，关闭',
  },
  form: {
    heading: '提交工单',
    intro: '告诉我们具体情况，你可以在这里跟进处理进度。',
    category: '类别',
    selectCategory: '选择类别',
    avatar: '关联人物',
    avatarNone: '不针对某个具体人物',
    avatarHint: '我们会把这个人物的 ID 附在工单里，方便团队排查这个人物。',
    avatarLoading: '正在加载你的人物…',
    avatarEmpty: '还没有已生成完成的自定义人物',
    avatarLoadFailed: '人物列表加载失败。',
    avatarRefreshFailed: '人物列表刷新失败，当前显示上次加载的列表。',
    avatarDescriptionTooLong: '关联人物后，描述最多 {max} 个字（当前 {count} 个）。请精简描述，或选择“不针对某个具体人物”。',
    title: '标题',
    titlePlaceholder: '简要描述问题',
    description: '描述',
    descriptionPlaceholder: '发生了什么？你期望的结果是什么？补充能帮助我们复现的步骤。',
    environment: '环境信息',
    optional: '可选',
    platforms: '平台',
    integrationModes: '接入方式',
    clearDraft: '清空草稿',
    submit: '提交工单',
    submitting: '提交中…',
  },
  chatForm: {
    heading: '发起会话',
    intro: '提问或分享想法，Spatius 会在这里回复你。',
    subject: '主题',
    subjectPlaceholder: '想聊什么？',
    subjectRequired: '请填写主题。',
    message: '消息',
    messagePlaceholder: '输入你的消息…',
    messageRequired: '请输入消息或添加附件。',
    clearDraft: '清空草稿',
    submit: '发送',
    submitting: '发送中…',
  },
  tags: {
    publicAvatarApply: '公开数字人申请',
  },
}
